import { useContext, useState, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  Select,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  FormHelperText,
  Box,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import moment from "moment";
//Icons
import { useMutation, useQuery } from "@apollo/client";
//Import from project
import "../../Style/dialogstyle.scss";
import UploadImage from "../../Utils/UploadImage";
import { AuthContext } from "../../Context/AuthContext";
import { SelectDatePicker } from "../../Components/Include/SelectDatePicker/SelectDatePicker";
import { DialogTitleComp } from "../../Utils/Component";
import { useDeleteImageServer } from "../CropImage/DeleteImageServer";
import {
  CREATE_STUDENT,
  UPDATE_STUDENT,
  GET_NEXT_STUDENT_ID,
} from "../../Schema/Student";

export default function StudentForm({
  open,
  editData,
  setRefetch,
  handleClose,
  hideBackdrop,
  dialogTitle,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);

  const { data } = useQuery(GET_NEXT_STUDENT_ID);

  // =================================== Upload Image ====================================
  const [profileHook, setProfileHook] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const mutationDelete = useDeleteImageServer();
  const [loading, setLoading] = useState(false);

  const handleCloseAction = () => {
    handleClose();
    if (profileHook) {
      // delete iamge from server ====================================================
      let splitSrc = profileHook?.split(":")[4];
      mutationDelete.mutate({
        storage: "sr_music_academy",
        folder: "sr_music_images",
        file: splitSrc.split("/")[0],
      });
    }
    setProfileHook("");
    setImageFile("");
  };

  const [createStudent] = useMutation(CREATE_STUDENT, {
    onCompleted: ({ createStudent }) => {
      setLoading(false);
      if (createStudent?.isSuccess === true) {
        setAlert(true, "success", createStudent?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createStudent?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateStudent] = useMutation(UPDATE_STUDENT, {
    onCompleted: ({ updateStudent }) => {
      setLoading(false);
      // console.log("updateStudent::", updateStudent);
      if (updateStudent?.isSuccess === true) {
        setAlert(true, "success", updateStudent?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateStudent?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const validationSchema = Yup.object().shape({
    dob: Yup.string().required(t("required")),
    // email: Yup.string(),
    startSchoolDate: Yup.string().required(t("required")),
    gender: Yup.string().required(t("required")),
    address: Yup.string(),
    // studentId: Yup.string().required(t("required")),
    familyName: Yup.string().required(t("required")),
    nationlity: Yup.string(),
    phoneNumber: Yup.string(),
    studentName: Yup.string().required(t("required")),
  });

  const formik = useFormik({
    initialValues: {
      dob: "",
      email: "",
      gender: "Male",
      address: "",
      startSchoolDate: "",
      // studentId: "",
      familyName: "",
      nationlity: "",
      phoneNumber: "",
      studentName: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // console.log("values::", values);
      setLoading(true);
      if (dialogTitle === "Update") {
        updateStudent({
          variables: {
            id: editData?._id,
            input: {
              ...values,
              startSchoolDate: moment(values?.startSchoolDate).format(
                "YYYY-MM-DD",
              ),
              profile: profileHook ? profileHook : editData?.profile,
            },
          },
        });
      } else {
        createStudent({
          variables: {
            input: {
              ...values,
              studentId: data?.getNextStudentId,
              startSchoolDate: moment(values?.startSchoolDate).format(
                "YYYY-MM-DD",
              ),
              profile: profileHook || "",
            },
          },
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    resetForm,
    handleSubmit,
    setFieldValue,
    getFieldProps,
  } = formik;

  useEffect(() => {
    if (dialogTitle === "Create") {
      resetForm();
      return;
    }
    if (editData) {
      setFieldValue("dob", editData?.dob || "");
      setFieldValue("email", editData?.email || "");
      setFieldValue("startSchoolDate", editData?.startSchoolDate || "");
      setFieldValue("gender", editData?.gender || "");
      setFieldValue("address", editData?.address || "");
      setFieldValue("studentId", editData?.studentId || "");
      setFieldValue("familyName", editData?.familyName || "");
      setFieldValue("nationlity", editData?.nationlity || "");
      setFieldValue("phoneNumber", editData?.phoneNumber || "");
      setFieldValue("studentName", editData?.studentName || "");
    }
  }, [editData, open]);

  return (
    <Dialog
      open={open}
      className="dialog-container"
      maxWidth="sm"
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_student")
            : t("modal_title_update_student")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleCloseAction}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12}>
                <UploadImage
                  editData={editData}
                  imageFile={imageFile}
                  setImageFile={setImageFile}
                  setProfileHook={setProfileHook}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_last_name")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  size="small"
                  className="text-field"
                  placeholder={t("thead_last_name")}
                  {...getFieldProps("familyName")}
                  error={Boolean(touched?.familyName && errors?.familyName)}
                  helperText={touched?.familyName && errors?.familyName}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_first_name")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  size="small"
                  className="text-field"
                  placeholder={t("thead_first_name")}
                  {...getFieldProps("studentName")}
                  error={Boolean(touched.studentName && errors.studentName)}
                  helperText={touched.studentName && errors.studentName}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_id")}
                </Typography>
                {/* <TextField
                  fullWidth
                  multiline
                  size="small"
                  className="text-field"
                  disabled={true}
                  // placeholder={t("thead_id")}
                  // {...getFieldProps("studentId")}
                  // error={Boolean(touched.studentId && errors.studentId)}
                  // helperText={touched.studentId && errors.studentId}
                /> */}
                <Box
                  sx={{
                    width: "100%",
                    height: "40px",
                    bgcolor: "#F2F2F2",
                    padding: "8.5px 14px",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    className={t("class-name-field-title")}
                    sx={{ fontWeight: "bold" }}
                  >
                    {dialogTitle === "Create"
                      ? data?.getNextStudentId
                      : editData?.studentId}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_date_of_birth")}
                </Typography>

                <SelectDatePicker
                  value={values?.dob}
                  onChange={(val) => setFieldValue("dob", val)}
                />
                {!!errors.dob && touched.dob && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.dob}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_telephone")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  size="small"
                  className="text-field"
                  placeholder={t("label_telephone")}
                  {...getFieldProps("phoneNumber")}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_start_school_date")}
                </Typography>

                <SelectDatePicker
                  value={values?.startSchoolDate}
                  onChange={(val) => setFieldValue("startSchoolDate", val)}
                />
                {!!errors.startSchoolDate && touched.startSchoolDate && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.startSchoolDate}
                  </FormHelperText>
                )}
              </Grid>

              {/* <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_email")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("label_email")}
                  {...getFieldProps("email")}
                  error={Boolean(touched?.email && errors?.email)}
                  helperText={touched?.email && errors?.email}
                />
              </Grid> */}

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_nationality")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("thead_nationality")}
                  {...getFieldProps("nationlity")}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_gender")}
                </Typography>
                <FormControl fullWidth size="small" className="text-field">
                  <Select {...getFieldProps("gender")}>
                    <MenuItem value={"Male"}> {t("thead_male")} </MenuItem>
                    <MenuItem value={"Female"}>{t("thead_female")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_address")}
                </Typography>
                <TextField
                  minRows={2}
                  multiline
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("label_address")}
                  {...getFieldProps("address")}
                />
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    onClick={handleSubmit}
                    className={t("class-btn-create")}
                    // disabled={statusProgress && progress < 100 ? true : false}
                  >
                    {dialogTitle === "Update" ? t("update") : t("create")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
